import type { Source } from "@/types/api"

/** Duration of the highlight on a Sources row after a citation badge was clicked. */
export const SOURCE_FLASH_MS = 1500

/**
 * Comparison key for a URL, mirroring the backend's `_normalize_url`
 * (chat_service.py): scheme/host/path compared case-insensitively, path
 * unquoted without trailing slash, fragment dropped. Query parameters are
 * normally dropped except on SharePoint's Doc.aspx and Outlook web (/owa) URLs,
 * where they identify the item; the UI's `&web=1` viewer flag is ignored there too.
 */
export function normalizeUrl(url: string): string {
  const trimmed = url.trim()
  try {
    const parts = new URL(trimmed)
    let path = parts.pathname
    try {
      path = decodeURIComponent(path)
    } catch {
      // keep the raw path when it holds a malformed escape sequence
    }
    const normalizedPath = path.replace(/\/+$/, "")
    const params = new URLSearchParams(parts.search)
    params.delete("web")
    params.sort()
    const identifiedByQuery = /(?:^|\/)Doc\.aspx$/i.test(normalizedPath) || /^\/owa$/i.test(normalizedPath)
    const query = identifiedByQuery && params.size ? `?${params}` : ""
    return `${parts.protocol}//${parts.host}${normalizedPath}${query}`.toLowerCase()
  } catch {
    // Not an absolute URL: still strip fragment/query/trailing slash.
    const withoutFragment = trimmed.replace(/#.*$/, "")
    const docQuery = withoutFragment.match(/^(.*\/Doc\.aspx)(\?[^#]*)$/i)
    const query = docQuery?.[2]
      .slice(1)
      .split("&")
      .filter((param) => param.toLowerCase() !== "web=1")
      .sort()
    return (docQuery ? `${docQuery[1]}${query?.length ? `?${query.join("&")}` : ""}` : withoutFragment.replace(/\?.*$/, ""))
      .replace(/\/+$/, "")
      .toLowerCase()
  }
}

/**
 * 1-based number of the source `href` points to, as displayed in the Sources
 * list (index + 1 of the same array), or null when no source matches.
 * If several sources share a URL, the first one wins.
 */
export function matchSourceIndex(href: string | undefined, sources: Source[] | undefined): number | null {
  if (!href || !sources?.length) return null
  const key = normalizeUrl(href)
  const index = sources.findIndex((s) => s.url && s.url !== "#" && normalizeUrl(s.url) === key)
  return index === -1 ? null : index + 1
}

/** DOM id of a Sources row; scoped by message so several answers on screen don't collide. */
export function sourceRowId(messageId: string, n: number): string {
  return `src-${messageId}-${n}`
}

/** Window event asking the SourceList of a message to expand the group holding row `n`. */
export const SOURCE_FOCUS_EVENT = "source-focus"

export interface SourceFocusDetail {
  messageId: string
  n: number
}

function flashRow(row: HTMLElement): void {
  row.scrollIntoView({ block: "nearest", behavior: "smooth" })
  row.classList.remove("source-flash")
  void row.offsetWidth // restart the animation when clicked again
  row.classList.add("source-flash")
  window.setTimeout(() => row.classList.remove("source-flash"), SOURCE_FLASH_MS)
}

/**
 * Scroll the Sources row for citation `n` into view and flash it briefly.
 * If the row is not in the DOM (its group is collapsed), ask the SourceList to
 * expand that group first, then retry on the next frame.
 */
export function focusSourceRow(messageId: string, n: number): void {
  const id = sourceRowId(messageId, n)
  const row = document.getElementById(id)
  if (row) {
    flashRow(row)
    return
  }
  window.dispatchEvent(new CustomEvent<SourceFocusDetail>(SOURCE_FOCUS_EVENT, { detail: { messageId, n } }))
  window.requestAnimationFrame(() => {
    const expanded = document.getElementById(id)
    if (expanded) flashRow(expanded)
  })
}
